import type { SupabaseClient } from '@supabase/supabase-js';

export interface ListingReviewSettings {
  autoApproveListings: boolean;
  listingReviewHours: number;
  /** Free trial length on paid plans. 0 = no trial. */
  subscriptionTrialDays: number;
  /** Whether the competitor comparison table appears on the membership page. */
  showComparisonTable: boolean;
}

export const DEFAULT_REVIEW_SETTINGS: ListingReviewSettings = {
  autoApproveListings: false,
  listingReviewHours: 48,
  subscriptionTrialDays: 0,
  showComparisonTable: true,
};

/**
 * How new listings are handled. Read from the database so the review window
 * quoted on the public pages always matches what is actually enforced by the
 * listings_enforce_review trigger.
 */
export async function fetchReviewSettings(
  supabase: SupabaseClient
): Promise<ListingReviewSettings> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('auto_approve_listings, listing_review_hours, subscription_trial_days, show_comparison_table')
    .eq('id', 1)
    .maybeSingle();

  if (error || !data) return DEFAULT_REVIEW_SETTINGS;

  return {
    autoApproveListings: Boolean(data.auto_approve_listings),
    listingReviewHours: Number(data.listing_review_hours) || 48,
    subscriptionTrialDays: Number(data.subscription_trial_days) || 0,
    showComparisonTable: data.show_comparison_table !== false,
  };
}

/** Admin-only: switch between hand approval and auto-publish. */
export async function setAutoApprove(
  supabase: SupabaseClient,
  autoApprove: boolean
): Promise<void> {
  const { error } = await supabase
    .from('site_settings')
    .update({ auto_approve_listings: autoApprove, updated_at: new Date().toISOString() })
    .eq('id', 1);
  if (error) throw error;
}

/** Admin-only: turn the free trial on (a number of days) or off (0). */
export async function setTrialDays(
  supabase: SupabaseClient,
  days: number
): Promise<void> {
  const { error } = await supabase
    .from('site_settings')
    .update({ subscription_trial_days: Math.max(0, Math.round(days)), updated_at: new Date().toISOString() })
    .eq('id', 1);
  if (error) throw error;
}

/** Admin-only: show or hide the competitor comparison table. */
export async function setShowComparisonTable(
  supabase: SupabaseClient,
  show: boolean
): Promise<void> {
  const { error } = await supabase
    .from('site_settings')
    .update({ show_comparison_table: show, updated_at: new Date().toISOString() })
    .eq('id', 1);
  if (error) throw error;
}

export interface ExtraPrices {
  boostPence: number;
  haulageJobPence: number;
}

export const DEFAULT_EXTRA_PRICES: ExtraPrices = {
  boostPence: 2000,
  haulageJobPence: 2000,
};

/**
 * What the paid extras cost.
 *
 * Read from the database rather than hardcoded so Alex can change £20 without a
 * deploy. The server re-reads these when creating the Checkout session, so a
 * stale figure here can only ever mislabel a button, never mischarge anyone.
 */
export async function fetchExtraPrices(supabase: SupabaseClient): Promise<ExtraPrices> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('boost_price_pence, haulage_job_price_pence')
    .eq('id', 1)
    .maybeSingle();
  if (error || !data) return DEFAULT_EXTRA_PRICES;
  return {
    boostPence: Number(data.boost_price_pence) || DEFAULT_EXTRA_PRICES.boostPence,
    haulageJobPence: Number(data.haulage_job_price_pence) || DEFAULT_EXTRA_PRICES.haulageJobPence,
  };
}

export async function saveExtraPrices(
  supabase: SupabaseClient,
  patch: Partial<ExtraPrices>
): Promise<void> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.boostPence !== undefined) row.boost_price_pence = patch.boostPence;
  if (patch.haulageJobPence !== undefined) row.haulage_job_price_pence = patch.haulageJobPence;
  const { error } = await supabase.from('site_settings').update(row).eq('id', 1);
  if (error) throw error;
}
